import {
  Box,
  Breadcrumbs,
  DateFormats,
  Dropdown,
  formatISODate,
  Inline,
  isRedacted,
  Notification,
  type OptionType,
  Stack,
  Tag,
  type TagIntent,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useGetManagerEmployeesSimplified,
  useGetTimesheet,
} from '@omnipresentgroup/domains';
import {
  TIMESHEET_ACTIONS_ID,
  TimesheetDetails,
  TimesheetDetailsNavigation,
} from '@omnipresentgroup/features';
import { sortObjectsByKey } from '@omnipresentgroup/helpers';
import {
  type TimesheetArbeitszeit,
  type TimesheetArbeitszeitStatusEnum,
  TimesheetPageActions,
  TimesheetsSystem,
} from '@omnipresentgroup/types';
import { DateTime } from 'luxon';
import { memo, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { HOME_PATH } from '../../constants';
import { useManagerUser } from '../../hooks';

const PAGE_TITLE = 'Timesheet';
const NOTICE_TITLE =
  'Please note this interface is currently only for German employees';

const today = DateTime.now().toISODate();

export const Timesheet = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const employeeId = new URLSearchParams(location.search).get(
    'employeeId',
  ) as string;
  const weekDate = new URLSearchParams(location.search).get(
    'weekDate',
  ) as string;

  const { data: timesheet } = useGetTimesheet({
    employeeId,
    weekDate: weekDate || today,
    enabled: !!employeeId,
  });

  const Title = ({ status }: { status?: TimesheetArbeitszeit['status'] }) => {
    const { type, updatedAt } = status || {};

    const intentPerStatus: Record<TimesheetArbeitszeitStatusEnum, TagIntent> = {
      draft: 'default',
      submitted: 'info',
      changes_requested: 'warning',
      approved: 'success',
    };

    const labelPerStatus: Record<TimesheetArbeitszeitStatusEnum, string> = {
      draft: 'Draft',
      submitted: 'Submitted',
      changes_requested: 'Changes requested',
      approved: 'Approved',
    };

    return (
      <Stack gap="0">
        <Inline gap="8" align="center" minH={40}>
          <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
            {PAGE_TITLE}
          </Typography>
          {type && (
            <Tag id="timesheet-status" intent={intentPerStatus[type]}>
              {labelPerStatus[type]}
            </Tag>
          )}
        </Inline>
        <Typography
          as="span"
          hideParagraphSpacing
          size="14"
          weight="regular"
          color="subtle"
        >
          Last update at{' '}
          {updatedAt ? formatISODate(updatedAt, DateFormats.DateTime) : '-'}
        </Typography>
      </Stack>
    );
  };

  const EmployeeSelector = memo(() => {
    const { companyId } = useManagerUser();

    const { data: employeesData, isLoading: isLoadingEmployees } =
      useGetManagerEmployeesSimplified({
        companyId,
        timesheetsSystem: TimesheetsSystem.ARBEITSZEIT,
        enabled: !!companyId,
      });

    const employees = useMemo(() => {
      const filteredData = employeesData?.employees?.filter(
        (employee) => employee.onboardingStatus === 'Active',
      );
      return filteredData
        ? sortObjectsByKey({ key: 'name', list: filteredData })
        : [];
    }, [employeesData?.employees]);

    const options: OptionType[] = useMemo(
      () =>
        employees?.map((employee) => ({
          value: employee.id,
          label: employee.name,
          description: isRedacted(employee.email) ? 'Redacted' : employee.email,
        })) || [],
      [employees],
    );

    return (
      <Inline maxW={400} w="stretch" stretch={1}>
        <Dropdown
          id="employee-dropdown"
          placeholder="Select an employee"
          size="small"
          isMulti={false}
          options={options}
          value={options.find((option) => option.value === employeeId)}
          onChange={(selectedEmployee) => {
            const queryParameters = new URLSearchParams(location.search);
            queryParameters.set(
              'employeeId',
              selectedEmployee?.value?.toString() || '',
            );
            navigate(`?${queryParameters.toString()}`, {
              replace: true,
            });
          }}
          isLoading={isLoadingEmployees}
          isSearchable
        />
      </Inline>
    );
  });

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate(HOME_PATH) },
            {
              id: 'timesheet',
              title: PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <Stack gap="24" id="page-header">
          <Inline justify="space-between">
            <Title status={timesheet?.status} />
            <Box id={TIMESHEET_ACTIONS_ID} />
          </Inline>
        </Stack>
      </Stack>
      <Notification id="timesheet-notice" title={NOTICE_TITLE} />
      <Inline justify="space-between" css={{ flexWrap: 'wrap' }}>
        <EmployeeSelector />
        <TimesheetDetailsNavigation timesheet={timesheet} />
      </Inline>
      {employeeId && (
        <TimesheetDetails
          employeeId={employeeId}
          weekDate={weekDate}
          columnVisibility={{
            date: true,
            timeSlots: true,
            summary: true,
          }}
          readonly={true}
          actions={[
            TimesheetPageActions.REQUEST_CHANGES,
            TimesheetPageActions.APPROVE,
          ]}
        />
      )}
    </>
  );
};
